// SOC-NAICS API Service for Frontend Integration
// Connects to the FastAPI backend with runtime configuration support

import { ApiResponse, SOCOccupation, Job, Industry } from "../types";
import { getApiBaseUrl, getApiKey } from "../utils/config";

class SOCApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Get API configuration dynamically
    const baseUrl = getApiBaseUrl();
    const apiKey = getApiKey();
    
    // Ensure proper URL construction without double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${baseUrl}${cleanEndpoint}`;
    
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`,
        ...options.headers,
      },
      ...options,
      // Note: In production, remove this and use proper SSL certificates
      mode: 'cors',
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private toNumber(value?: number | string | null): number | undefined {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const cleaned = String(value).replace(/[^0-9.-]/g, "");
    if (!cleaned) return undefined;
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private formatRange(min?: number, max?: number): string | undefined {
    if (
      min === undefined ||
      max === undefined ||
      !Number.isFinite(min) ||
      !Number.isFinite(max)
    ) {
      return undefined;
    }
    return `$${Math.round(min).toLocaleString()} - $${Math.round(max).toLocaleString()}`;
  }

  // Transform SOC occupation to Job format
  transformToJob(occupation: SOCOccupation, index: number): Job {
    const salaryPct10_2023 = this.toNumber(occupation["2023 Pct. 10 Annual Earnings_Lightcast"]);
    const salaryPct90_2023 = this.toNumber(occupation["2023 Pct. 90 Annual Earnings_Lightcast"]);
    const hourlyPct10_2023 = this.toNumber(occupation["2023 Pct. 10 Hourly Earnings_Lightcast"]);
    const hourlyPct90_2023 = this.toNumber(occupation["2023 Pct. 90 Hourly Earnings_Lightcast"]);

    const salaryPct10 =
      salaryPct10_2023 ??
      this.toNumber(occupation["COL Adjusted Pct. 10 Annual Earnings_Lightcast"]);
    const salaryPct90 =
      salaryPct90_2023 ??
      this.toNumber(occupation["COL Adjusted Pct. 90 Annual Earnings_Lightcast"]);
    const hourlyPct10 =
      hourlyPct10_2023 ??
      this.toNumber(occupation["COL Adjusted Pct. 10 Hourly Earnings_Lightcast"]);
    const hourlyPct90 =
      hourlyPct90_2023 ??
      this.toNumber(occupation["COL Adjusted Pct. 90 Hourly Earnings_Lightcast"]);

    const salaryRange10to90 = this.formatRange(salaryPct10, salaryPct90);
    const hourlyRange10to90 = this.formatRange(hourlyPct10, hourlyPct90);

    const computedSalaryRange =
      salaryRange10to90 || occupation["Annual Salary Range"] || "Not specified";

    return {
      id: occupation.row_index?.toString() || `job-${index}`,
      title: occupation.occupation_title || occupation["Industry Title"] || "Unknown Occupation",
      socCode: occupation.SOC_Code,
      sector: occupation.sector || "Other",
      careerCluster: occupation.career_cluster || "Other",
      educationRequirement: occupation.education_requirement || "Not specified",
      estimatedSalary: occupation["Estimated Annual Salary (COL Adjusted)"],
      medianWage: occupation["Median Hourly Wage (COL Adjusted)"],
      salaryPct10,
      salaryPct90,
      hourlyPct10,
      hourlyPct90,
      salaryRange10to90,
      hourlyRange10to90,
      employment: occupation["2023 Jobs_Lightcast"],
      projectedEmployment: occupation["2026 Jobs_Lightcast"],
      employmentChange: (occupation["2026 Jobs_Lightcast"] || 0) - (occupation["2023 Jobs_Lightcast"] || 0),
      employmentPercentChange: (() => {
        const current = occupation["2023 Jobs_Lightcast"];
        const projected = occupation["2026 Jobs_Lightcast"] || 0;
        
        // Handle edge cases: if current is 0, null, or undefined, return 0
        if (!current || current === 0) {
          return 0;
        }
        
        // Calculate percentage change (3-year growth: 2023 → 2026)
        const percentChange = ((projected - current) / current) * 100;
        
        // Cap extreme percentages to prevent unrealistic values
        const cappedChange = Math.max(-99, Math.min(99, percentChange));
        
        return Math.round(cappedChange * 100) / 100; // Round to 2 decimal places
      })(),
      focusAreas: [occupation.sector || "Other"],
      description: occupation.description || (occupation.ooh_text && occupation.ooh_text !== "BLS Occupational Outlook Handbook" ? occupation.ooh_text : null) || "Job description not available",
      jobPostingDuration: occupation.job_posting,
      uniquePosting: this.toNumber(occupation.unique_posting),  // Legacy field
      latest365DaysPostings:
        this.toNumber(occupation.latest_365_days_postings) ??
        this.toNumber(occupation.unique_posting),
      latest90DaysPostings: this.toNumber(occupation.latest_90_days_postings),
      latest30DaysPostings: this.toNumber(occupation.latest_30_days_postings),
      // Education and training fields
      workExperience: occupation.work_experience || "Not specified",
      onJobTraining: occupation.on_job_training || "Not specified",
      // Legacy fields for compatibility
      cluster: this.mapSectorToCluster(occupation.sector || "Other"),
      focusArea: this.mapSectorToFocusArea(occupation.sector || "Other"),
      education: occupation.education_requirement || "Not specified",
      salary: `$${Math.round(occupation["Estimated Annual Salary (COL Adjusted)"] || 0).toLocaleString()}`,
      hourlyWage: `$${Math.round(occupation["Median Hourly Wage (COL Adjusted)"] || 0)}`,
      salaryRange: computedSalaryRange,
      openings2020: Math.round(occupation["2020 Jobs_Lightcast"] || 0),
      openings2021: Math.round(occupation["2021 Jobs_Lightcast"] || 0),
      openings2022: Math.round(occupation["2022 Jobs_Lightcast"] || 0),
      openings2023: Math.round(occupation["2023 Jobs_Lightcast"] || 0),
      openings2024: Math.round(occupation["2024 Jobs_Lightcast"] || 0),
      openings2025: Math.round(occupation["2025 Jobs_Lightcast"] || 0),
      openings2026: Math.round(occupation["2026 Jobs_Lightcast"] || 0),
      openings2027: Math.round(occupation["2027 Jobs_Lightcast"] || 0),
      openings2028: Math.round(occupation["2028 Jobs_Lightcast"] || 0),
      openings2029: Math.round(occupation["2029 Jobs_Lightcast"] || 0),
      openings2030: Math.round(occupation["2030 Jobs_Lightcast"] || 0),
      openings2031: Math.round(occupation["2031 Jobs_Lightcast"] || 0),
      openings2032: Math.round(occupation["2032 Jobs_Lightcast"] || 0),
      openings2033: Math.round(occupation["2033 Jobs_Lightcast"] || 0),
      industry: occupation["Industry Title"] || "Not specified",
      currentYear: occupation.current_year || 0,
      dataWindow: `${occupation.data_window_start || 'N/A'} - ${occupation.data_window_end || 'N/A'}`,
      oohUrl: occupation.ooh_url,
      oohText: occupation.ooh_text,
      important: false,
      // O*NET comprehensive data fields
      skills: occupation.skills,
      technology: occupation.technology,
      tools: occupation.tools,
      knowledge: occupation.knowledge,
    };
  }

  // Map API sectors to frontend clusters
  private mapSectorToCluster(sector: string): string {
    switch (sector.toLowerCase()) {
      case 'healthcare':
        return 'Health Science';
      case 'aviation':
        return 'Transportation';
      case 'education':
        return 'Education & Training';
      default:
        return 'Other';
    }
  }

  // Map API sectors to frontend focus areas
  private mapSectorToFocusArea(sector: string): string {
    switch (sector.toLowerCase()) {
      case 'healthcare':
        return 'Therapeutic Services';
      case 'aviation':
        return 'Aviation';
      case 'education':
        return 'Teaching/Training';
      default:
        return 'General';
    }
  }

  // Get all occupations with optional filtering
  async getOccupations(params: {
    limit?: number;
    skip?: number;
    sector?: string;
    career_cluster?: string;
    education_level?: string;
    min_salary?: number;
    max_salary?: number;
    sort_by?: string;
    min_current_jobs?: number;
  } = {}): Promise<ApiResponse<SOCOccupation>> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.skip) queryParams.append('skip', params.skip.toString());
    if (params.sector) queryParams.append('sector', params.sector);
    if (params.career_cluster) queryParams.append('career_cluster', params.career_cluster);
    if (params.education_level) queryParams.append('education_level', params.education_level);
    if (params.min_salary) queryParams.append('min_salary', params.min_salary.toString());
    if (params.max_salary) queryParams.append('max_salary', params.max_salary.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (typeof params.min_current_jobs === 'number') {
      queryParams.append('min_current_jobs', params.min_current_jobs.toString());
    }

    const response = await this.request<any>(`/api/soc/occupations?${queryParams}`);
    return {
      data: response.data || [],
      total: response.total || (response.data ? response.data.length : 0),
      limit: response.limit || params.limit || 100,
      skip: response.skip || params.skip || 0
    };
  }

  // Search occupations
  async searchOccupations(
    query: string,
    limit: number = 50,
    skip: number = 0,
    sort_by?: string,
    min_current_jobs?: number
  ): Promise<ApiResponse<SOCOccupation>> {
    const queryParams = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      skip: skip.toString(),
    });
    
    if (sort_by && sort_by !== 'default') {
      queryParams.append('sort_by', sort_by);
    }
    if (typeof min_current_jobs === 'number') {
      queryParams.append('min_current_jobs', min_current_jobs.toString());
    }

    const response = await this.request<any>(`/api/soc/search?${queryParams}`);
    return {
      data: response.data || [],
      total: response.total || (response.data ? response.data.length : 0),
      limit: response.limit || limit,
      skip: response.skip || skip
    };
  }

  // Get occupations by sector
  async getOccupationsBySector(sector: string, limit: number = 100): Promise<ApiResponse<SOCOccupation>> {
    return this.request<ApiResponse<SOCOccupation>>(
      `/api/soc/occupations/sector/${encodeURIComponent(sector)}?limit=${limit}`
    );
  }

  // Get occupations by education level
  async getOccupationsByEducation(educationLevel: string, limit: number = 100): Promise<ApiResponse<SOCOccupation>> {
    return this.request<ApiResponse<SOCOccupation>>(
      `/api/soc/occupations/education/${encodeURIComponent(educationLevel)}?limit=${limit}`
    );
  }

  // Get all sectors
  async getSectors(): Promise<{ sectors: string[] }> {
    return this.request<{ sectors: string[] }>('/api/soc/sectors');
  }

  // Get education levels
  async getEducationLevels(): Promise<{ education_levels: string[] }> {
    return this.request<{ education_levels: string[] }>('/api/soc/education-levels');
  }

  // Get top occupations
  async getTopOccupations(
    by: string = "occupation_title",
    limit: number = 20,
    sector?: string
  ): Promise<ApiResponse<SOCOccupation>> {
    const queryParams = new URLSearchParams({
      by,
      limit: limit.toString(),
    });
    if (sector) {
      queryParams.append('sector', sector);
    }

    return this.request<ApiResponse<SOCOccupation>>(`/api/soc/occupations/top?${queryParams}`);
  }

  // Get occupation by SOC code
  async getOccupationByCode(socCode: string): Promise<SOCOccupation | null> {
    const response = await this.request<any>(`/api/soc/occupations/${encodeURIComponent(socCode)}`);
    if (!response) {
      return null;
    }

    if (response.data && typeof response.data === 'object') {
      return response.data as SOCOccupation;
    }

    return response as SOCOccupation;
  }

  // Get detailed occupation information including BLS OOH data
  async getOccupationDetails(socCode: string): Promise<{
    success: boolean;
    data: {
      soc_code: string;
      title: string;
      sector: string;
      career_cluster: string;
      education_requirement: string;
      work_experience: string;
      on_job_training: string;
      salary: {
        annual: number;
        hourly: number;
        range: string;
      };
      employment: {
        current: number;
        projected: number;
        change: number;
        percent_change: number;
      };
      bls_ooh: {
        url: string;
        text: string;
        description: string;
      };
      job_posting_duration: number;
      employment_projections: {
        [key: string]: number;
      };
      metadata: {
        source_file: string;
        upload_timestamp: string;
        scraped_date: string;
      };
    };
    soc_code: string;
    timestamp: string;
  }> {
    return this.request(`/api/soc/occupations/${encodeURIComponent(socCode)}/details`);
  }

  // Get industries for a specific SOC code
  async getIndustriesBySOC(
    socCode: string, 
    limit: number = 50, 
    skip: number = 0,
    sortBy?: string
  ): Promise<{
    success: boolean;
    soc_code: string;
    industries: Industry[];
    count: number;
    total_count?: number;
    pagination?: {
      limit: number;
      skip: number;
      has_more: boolean;
      total_pages: number;
      current_page: number;
    };
    timestamp: string;
    message?: string;
  }> {
    // Note: Backend endpoint doesn't support pagination parameters yet
    return this.request(`/api/soc/industries/${encodeURIComponent(socCode)}`);
  }

  // Get sectors for frontend display
  async getFrontendSectors(): Promise<string[]> {
    try {
      const response = await this.request<{ sectors: string[] }>('/api/soc/sectors');
      const sectors = response.sectors || [];
      // Add "All" as the first option
      return ['all', ...sectors];
    } catch (error) {
      console.error('Failed to fetch sectors from API:', error);
      // Fallback to the sectors we know exist
      return ['all', 'Aviation', 'Education', 'Healthcare', 'Other'];
    }
  }

  // Get all career clusters
  async getCareerClusters(): Promise<{ career_clusters: string[] }> {
    return this.request<{ career_clusters: string[] }>('/api/soc/career-clusters');
  }

  // Get occupations by career cluster
  async getOccupationsByCareerCluster(careerCluster: string, limit: number = 100): Promise<ApiResponse<SOCOccupation>> {
    return this.request<ApiResponse<SOCOccupation>>(
      `/api/soc/occupations/career-cluster/${encodeURIComponent(careerCluster)}?limit=${limit}`
    );
  }

  // Get career clusters for frontend display
  async getFrontendCareerClusters(): Promise<string[]> {
    try {
      const response = await this.request<{ career_clusters: string[] }>('/api/soc/career-clusters');
      const careerClusters = response.career_clusters || [];
      // Add "All" as the first option
      return ['all', ...careerClusters];
    } catch (error) {
      console.error('Failed to fetch career clusters from API:', error);
      // Fallback to the career clusters we know exist
      return ['all', 'Marketing Sales / Management', 'Information Technology', 'Manufacturing', 'Architecture & Construction', 'Education & Training', 'Civics, Public Safety', 'Hospitality & Tourism', 'Finance', 'Marketing Sales', 'Supply Chain & Transportation', 'Other'];
    }
  }

  // Get businesses for a SOC code
  async getBusinessesBySOC(socCode: string, limit: number = 50): Promise<{
    soc_code: string;
    occupation_title: string;
    naics_codes: string[];
    mapped_bls_codes: string[];
    industries: Industry[];
    businesses: import('../types').BusinessItem[];
    total_businesses: number;
    limit: number;
    timestamp: string;
  }> {
    const qp = new URLSearchParams({ limit: String(limit) });
    return this.request(`/api/soc/businesses/${encodeURIComponent(socCode)}?${qp.toString()}`);
  }
}

export const socApiService = new SOCApiService();
export default socApiService; 